/**
 * CBO Program Setup Wizard
 * Wizard for CBOs to create programs - either linked to sponsor invitations or independent
 * Supports two models:
 * - Model 1: Sponsor-linked (accepts invitation, inherits milestones, creates custom workflows)
 * - Model 2: Independent (standalone program without sponsor linkage)
 */

import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Input } from '../ui/input';
import PhoneInput from '@/components/ui/PhoneInput';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { getWeaveinApiUrl } from '@/src/config';
import useWorkflowTemplates from '@/hooks/useWorkflows';
import { WorkflowTemplate } from '@/services/api/workflows';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { authFetch } from '@/lib/authFetch';
import { HealthPlanPicker } from '../programs/HealthPlanPicker';
import {
  CheckCircle2,
  Circle,
  ChevronRight,
  ChevronLeft,
  Target,
  Users,
  Building2,
  FileText,
  Workflow,
  Link,
  Unlink,
  Mail,
  Calendar,
  DollarSign,
  Award,
  AlertCircle
} from 'lucide-react';

interface CBOProgramSetupWizardProps {
  cboId: number;
  onComplete?: (programId: number) => void;
  onCancel?: () => void;
}

interface SponsorInvitation {
  invitation_id: number;
  sponsor_id: number;
  sponsor_name: string;
  program_id: number;
  program_name: string;
  program_type: string;
  terms_id: number;
  match_score: number;
  invitation_date: string;
  invitation_message: string;
  expires_at: string | null;
}

interface SponsorMilestone {
  milestone_id: number;
  milestone_name: string;
  milestone_type: string;
  target_metric_name: string;
  target_value: number;
  target_unit: string;
  target_date: string;
  has_incentive: boolean;
  incentive_amount: number;
  cbo_payout_amount: number;
  member_payout_amount: number;
}


interface ProgramFormData {
  name: string;
  program_type: string;
  description: string;
  start_date: string;
  end_date: string;
  capacity: number | null;
  funding_source: string;
  /** TRUS-725: the health plan this program bills (payers.id). */
  payer_id: number | null;
  services_offered: string[];
  program_manager_name: string;
  program_manager_email: string;
  program_manager_phone: string;
}

interface SponsorTerms {
  id: number;
  terms_name: string;
  base_enrollment_incentive: number;
  per_member_rate: number;
  milestone_bonus_structure: any;
  payment_schedule: string;
  minimum_enrollments: number;
  minimum_completion_rate: number;
}

const WIZARD_STEPS = [
  {
    id: 1,
    name: 'Program Model',
    icon: Link,
    description: 'Choose program type',
    guidance: 'Select whether this program is linked to a sponsor invitation or an independent CBO program.'
  },
  {
    id: 2,
    name: 'Select Invitation',
    icon: Mail,
    description: 'Choose sponsor invitation',
    guidance: 'Select from your pending sponsor invitations. This links your program to the sponsor\'s requirements.',
    sponsorLinkedOnly: true
  },
  {
    id: 3,
    name: 'Program Info',
    icon: FileText,
    description: 'Basic details',
    guidance: 'Configure your program details. Some fields may be pre-filled from the sponsor invitation.'
  },
  {
    id: 4,
    name: 'Sponsor Milestones',
    icon: Target,
    description: 'Review requirements',
    guidance: 'Review the milestones and incentives defined by the sponsor. Your workflows should support achieving these.',
    sponsorLinkedOnly: true
  },
  {
    id: 5,
    name: 'Workflows',
    icon: Workflow,
    description: 'Select templates',
    guidance: 'Select existing workflow templates to associate with this program. Templates can be created and managed in the Workflows section.'
  },
  {
    id: 6,
    name: 'Review',
    icon: CheckCircle2,
    description: 'Launch program',
    guidance: 'Review all settings and launch your program.'
  }
];

// Fallback types used while API types are loading
const DEFAULT_PROGRAM_TYPES = [
  { value: 'CalAIM_ECM', label: 'CalAIM Enhanced Care Management' },
  { value: 'CalAIM_CS', label: 'CalAIM Community Supports' },
  { value: 'Housing', label: 'Housing Services' },
  { value: 'Food', label: 'Food & Nutrition' },
  { value: 'Transportation', label: 'Transportation' },
  { value: 'Organizing', label: 'Organizing' },
  { value: 'Other', label: 'Other' },
];

const SERVICES = [
  'care_coordination', 'health_promotion', 'comprehensive_assessment',
  'care_plan_development', 'referral_assistance', 'follow_up_care',
  'benefits_navigation', 'community_resources', 'housing_navigation',
  'food_assistance', 'transportation', 'mental_health_support',
  'substance_abuse_support', 'chronic_disease_management'
];


export const CBOProgramSetupWizard: React.FC<CBOProgramSetupWizardProps> = ({
  cboId,
  onComplete,
  onCancel
}) => {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Program types fetched from API
  const [programTypes, setProgramTypes] = useState<{ value: string; label: string }[]>(DEFAULT_PROGRAM_TYPES);

  useEffect(() => {
    const fetchProgramTypes = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await authFetch(getWeaveinApiUrl('/api/programs/types/list'));
        if (response.ok) {
          const types = await response.json();
          setProgramTypes(types);
        }
      } catch (err) {
        console.error('Error fetching program types:', err);
      }
    };
    fetchProgramTypes();
  }, []);

  // Model selection
  const [programModel, setProgramModel] = useState<'SPONSOR_LINKED' | 'INDEPENDENT' | null>(null);

  // Sponsor invitations
  const [invitations, setInvitations] = useState<SponsorInvitation[]>([]);
  const [selectedInvitation, setSelectedInvitation] = useState<SponsorInvitation | null>(null);

  // Sponsor milestones (for sponsor-linked programs)
  const [sponsorMilestones, setSponsorMilestones] = useState<SponsorMilestone[]>([]);
  const [sponsorTerms, setSponsorTerms] = useState<SponsorTerms | null>(null);

  // Program data
  const [programData, setProgramData] = useState<ProgramFormData>({
    name: '',
    program_type: '',
    description: '',
    start_date: '',
    end_date: '',
    capacity: null,
    funding_source: '',
    payer_id: null,
    services_offered: [],
    program_manager_name: '',
    program_manager_email: '',
    program_manager_phone: ''
  });

  // Selected workflow templates
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<number[]>([]);

  // Created program ID
  const [createdProgramId, setCreatedProgramId] = useState<number | null>(null);

  // Fetch available workflow templates
  const {
    templates: availableTemplates,
    loading: templatesLoading,
  } = useWorkflowTemplates({
    is_active: true,
  });

  // Get visible steps based on program model
  const visibleSteps = WIZARD_STEPS.filter(step => {
    if (step.sponsorLinkedOnly && programModel !== 'SPONSOR_LINKED') {
      return false;
    }
    return true;
  });

  // Map current step to visible step index
  const getCurrentVisibleStep = () => {
    let visibleIndex = 0;
    for (let i = 0; i < WIZARD_STEPS.length; i++) {
      if (WIZARD_STEPS[i].id === currentStep) {
        return visibleIndex;
      }
      if (!WIZARD_STEPS[i].sponsorLinkedOnly || programModel === 'SPONSOR_LINKED') {
        visibleIndex++;
      }
    }
    return visibleIndex;
  };

  // Fetch pending invitations when component mounts
  useEffect(() => {
    if (programModel === 'SPONSOR_LINKED') {
      fetchInvitations();
    }
  }, [programModel, cboId]);

  // Fetch sponsor milestones when invitation is selected
  useEffect(() => {
    if (selectedInvitation) {
      fetchSponsorMilestones(selectedInvitation.program_id);
      fetchSponsorTerms(selectedInvitation.terms_id);
      // Pre-fill program data from invitation
      setProgramData(prev => ({
        ...prev,
        name: `${selectedInvitation.program_name} - CBO Delivery`,
        program_type: selectedInvitation.program_type,
        description: `CBO delivery program for ${selectedInvitation.sponsor_name}'s ${selectedInvitation.program_name}`
      }));
    }
  }, [selectedInvitation]);

  const fetchInvitations = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        console.error('No auth token found');
        setInvitations([]);
        setLoading(false);
        return;
      }

      const response = await fetch(getWeaveinApiUrl(`/api/cbo/${cboId}/pending-invitations`), {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-CBO-ID': cboId.toString()
        }
      });
      if (response.ok) {
        const data = await response.json();
        setInvitations(data);
      } else {
        // If endpoint doesn't exist yet, show empty state
        setInvitations([]);
      }
    } catch (err) {
      console.error('Error fetching invitations:', err);
      setInvitations([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSponsorMilestones = async (programId: number) => {
    try {
      const response = await fetch(getWeaveinApiUrl(`/api/programs/${programId}/milestones`), {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        setSponsorMilestones(data);
      }
    } catch (err) {
      console.error('Error fetching sponsor milestones:', err);
    }
  };

  const fetchSponsorTerms = async (termsId: number) => {
    try {
      const response = await fetch(getWeaveinApiUrl(`/api/sponsor-terms/${termsId}`), {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        setSponsorTerms(data);
      }
    } catch (err) {
      console.error('Error fetching sponsor terms:', err);
    }
  };

  const handleNext = async () => {
    setError(null);

    // Validate current step
    if (currentStep === 1 && !programModel) {
      setError('Please select a program model');
      return;
    }

    if (currentStep === 2 && programModel === 'SPONSOR_LINKED' && !selectedInvitation) {
      setError('Please select a sponsor invitation');
      return;
    }

    if (currentStep === 3 && !programData.name) {
      setError('Please enter a program name');
      return;
    }

    // Find next step
    let nextStep = currentStep + 1;
    while (nextStep <= 6) {
      const stepConfig = WIZARD_STEPS.find(s => s.id === nextStep);
      if (stepConfig?.sponsorLinkedOnly && programModel !== 'SPONSOR_LINKED') {
        nextStep++;
      } else {
        break;
      }
    }

    if (nextStep <= 6) {
      setCurrentStep(nextStep);
    }
  };

  const handleBack = () => {
    let prevStep = currentStep - 1;
    while (prevStep >= 1) {
      const stepConfig = WIZARD_STEPS.find(s => s.id === prevStep);
      if (stepConfig?.sponsorLinkedOnly && programModel !== 'SPONSOR_LINKED') {
        prevStep--;
      } else {
        break;
      }
    }

    if (prevStep >= 1) {
      setCurrentStep(prevStep);
    }
  };

  const handleLaunch = async () => {
    setLoading(true);
    setError(null);

    try {
      // Create the program
      const programPayload = {
        ...programData,
        cbo_id: cboId,
        program_model_type: programModel,
        sponsor_program_id: selectedInvitation?.program_id || null,
        sponsor_invitation_id: selectedInvitation?.invitation_id || null,
        sponsor_terms_id: selectedInvitation?.terms_id || null,
        is_active: true
      };

      const programResponse = await fetch(getWeaveinApiUrl('/api/programs'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CBO-ID': cboId.toString()
        },
        body: JSON.stringify(programPayload)
      });

      if (!programResponse.ok) {
        throw new Error('Failed to create program');
      }

      const program = await programResponse.json();
      setCreatedProgramId(program.id);

      // Associate selected workflow templates with the program
      if (selectedTemplateIds.length > 0) {
        for (const templateId of selectedTemplateIds) {
          await fetch(getWeaveinApiUrl(`/api/workflows/templates/${templateId}`), {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'X-CBO-ID': cboId.toString()
            },
            body: JSON.stringify({
              program_id: program.id
            })
          });
        }
      }

      // Accept the sponsor invitation if sponsor-linked
      if (selectedInvitation) {
        await fetch(getWeaveinApiUrl(`/api/sponsor-invitations/${selectedInvitation.invitation_id}/accept`), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CBO-ID': cboId.toString()
          },
          body: JSON.stringify({
            cbo_program_id: program.id
          })
        });
      }

      if (onComplete) {
        onComplete(program.id);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to launch program');
    } finally {
      setLoading(false);
    }
  };

  const toggleTemplateSelection = (templateId: number) => {
    setSelectedTemplateIds(prev =>
      prev.includes(templateId)
        ? prev.filter(id => id !== templateId)
        : [...prev, templateId]
    );
  };

  const toggleService = (service: string) => {
    setProgramData(prev => ({
      ...prev,
      services_offered: prev.services_offered.includes(service)
        ? prev.services_offered.filter(s => s !== service)
        : [...prev.services_offered, service]
    }));
  };

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderModelSelection();
      case 2:
        return renderInvitationSelection();
      case 3:
        return renderProgramInfo();
      case 4:
        return renderSponsorMilestones();
      case 5:
        return renderWorkflowTemplateSelector();
      case 6:
        return renderReview();
      default:
        return null;
    }
  };

  const renderModelSelection = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold mb-2">{t('cbo.choose_program_model', 'Choose Program Model')}</h2>
        <p className="text-gray-600">
          {t('cbo.select_how_this_program_will', 'Select how this program will be structured')}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Sponsor-Linked Model */}
        <Card
          className={`p-6 cursor-pointer transition-all ${
            programModel === 'SPONSOR_LINKED'
              ? 'border-2 border-blue-500 bg-blue-50'
              : 'border hover:border-blue-300'
          }`}
          onClick={() => setProgramModel('SPONSOR_LINKED')}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className={`p-3 rounded-lg ${
              programModel === 'SPONSOR_LINKED' ? 'bg-blue-500 text-white' : 'bg-gray-100'
            }`}>
              <Link className="h-6 w-6" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">{t('cbo.sponsorlinked_program', 'Sponsor-Linked Program')}</h3>
              <Badge variant="outline">{t('cbo.model_1', 'Model 1')}</Badge>
            </div>
          </div>
          <p className="text-gray-600 mb-4">
            Accept a sponsor invitation and deliver on their program requirements while
            creating your own workflows and outreach strategies.
          </p>
          <ul className="text-sm space-y-2">
            <li className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              {t('cbo.inherit_sponsor_milestones_incentives', 'Inherit sponsor milestones & incentives')}
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              {t('cbo.create_custom_delivery_workflows', 'Create custom delivery workflows')}
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              {t('cbo.track_progress_against_sponsor_goals', 'Track progress against sponsor goals')}
            </li>
          </ul>
        </Card>

        {/* Independent Model */}
        <Card
          className={`p-6 cursor-pointer transition-all ${
            programModel === 'INDEPENDENT'
              ? 'border-2 border-green-500 bg-green-50'
              : 'border hover:border-green-300'
          }`}
          onClick={() => setProgramModel('INDEPENDENT')}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className={`p-3 rounded-lg ${
              programModel === 'INDEPENDENT' ? 'bg-green-500 text-white' : 'bg-gray-100'
            }`}>
              <Unlink className="h-6 w-6" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">{t('cbo.independent_program', 'Independent Program')}</h3>
              <Badge variant="outline">{t('cbo.model_2', 'Model 2')}</Badge>
            </div>
          </div>
          <p className="text-gray-600 mb-4">
            Create a standalone program without sponsor linkage. Define your own
            milestones, workflows, and success criteria.
          </p>
          <ul className="text-sm space-y-2">
            <li className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              {t('cbo.full_control_over_program_design', 'Full control over program design')}
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              {t('cbo.define_your_own_milestones', 'Define your own milestones')}
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              {t('cbo.independent_funding_billing', 'Independent funding & billing')}
            </li>
          </ul>
        </Card>
      </div>
    </div>
  );

  const renderInvitationSelection = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold mb-2">{t('cbo.select_sponsor_invitation', 'Select Sponsor Invitation')}</h2>
        <p className="text-gray-600">
          {t('cbo.choose_from_your_pending_sponsor', 'Choose from your pending sponsor invitations')}
        </p>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p>{t('cbo.loading_invitations', 'Loading invitations...')}</p>
        </div>
      ) : invitations.length === 0 ? (
        <Card className="p-8 text-center">
          <Mail className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">{t('cbo.no_pending_invitations', 'No Pending Invitations')}</h3>
          <p className="text-gray-600 mb-4">
            You don't have any pending sponsor invitations. Check back later or contact
            sponsors directly to express interest in their programs.
          </p>
          <Button variant="outline" onClick={() => setProgramModel('INDEPENDENT')}>
            {t('cbo.create_independent_program_instead', 'Create Independent Program Instead')}
          </Button>
        </Card>
      ) : (
        <div className="space-y-4">
          {invitations.map(invitation => (
            <Card
              key={invitation.invitation_id}
              className={`p-4 cursor-pointer transition-all ${
                selectedInvitation?.invitation_id === invitation.invitation_id
                  ? 'border-2 border-blue-500 bg-blue-50'
                  : 'border hover:border-blue-300'
              }`}
              onClick={() => setSelectedInvitation(invitation)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <Building2 className="h-5 w-5 text-gray-500" />
                    <span className="font-semibold">{invitation.sponsor_name}</span>
                    <Badge>{invitation.program_type.replace(/_/g, ' ')}</Badge>
                  </div>
                  <h3 className="text-lg font-medium mb-1">{invitation.program_name}</h3>
                  {invitation.invitation_message && (
                    <p className="text-gray-600 text-sm mb-2">{invitation.invitation_message}</p>
                  )}
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      Invited: {fmt.short(invitation.invitation_date)}
                    </span>
                    {invitation.match_score && (
                      <span className="flex items-center gap-1">
                        <Target className="h-4 w-4" />
                        Match: {Math.round(invitation.match_score)}%
                      </span>
                    )}
                    {invitation.expires_at && (
                      <span className="flex items-center gap-1 text-orange-600">
                        <AlertCircle className="h-4 w-4" />
                        Expires: {fmt.short(invitation.expires_at)}
                      </span>
                    )}
                  </div>
                </div>
                <div className="ml-4">
                  {selectedInvitation?.invitation_id === invitation.invitation_id ? (
                    <CheckCircle2 className="h-6 w-6 text-blue-500" />
                  ) : (
                    <Circle className="h-6 w-6 text-gray-300" />
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );

  const renderProgramInfo = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold mb-2">{t('cbo.program_information', 'Program Information')}</h2>
        <p className="text-gray-600">
          {t('cbo.configure_your_program_details', 'Configure your program details')}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <Label htmlFor="name">{t('cbo.program_name', 'Program Name *')}</Label>
            <Input
              id="name"
              value={programData.name}
              onChange={e => setProgramData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., CalAIM ECM Outreach Program"
            />
          </div>

          <div>
            <Label htmlFor="program_type">{t('cbo.program_type', 'Program Type *')}</Label>
            <select
              id="program_type"
              className="w-full p-2 border rounded-md"
              value={programData.program_type}
              onChange={e => setProgramData(prev => ({ ...prev, program_type: e.target.value }))}
            >
              <option value="">{t('cbo.select_type', 'Select type...')}</option>
              {programTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <Label htmlFor="description">{t('cbo.description', 'Description')}</Label>
            <Textarea
              id="description"
              value={programData.description}
              onChange={e => setProgramData(prev => ({ ...prev, description: e.target.value }))}
              placeholder={t('cbo.describe_your_program_objectives_and', 'Describe your program objectives and approach...')}
              rows={4}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="start_date">{t('cbo.start_date', 'Start Date')}</Label>
              <Input
                id="start_date"
                type="date"
                value={programData.start_date}
                onChange={e => setProgramData(prev => ({ ...prev, start_date: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="end_date">{t('cbo.end_date', 'End Date')}</Label>
              <Input
                id="end_date"
                type="date"
                value={programData.end_date}
                onChange={e => setProgramData(prev => ({ ...prev, end_date: e.target.value }))}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="capacity">{t('cbo.enrollment_capacity', 'Enrollment Capacity')}</Label>
            <Input
              id="capacity"
              type="number"
              value={programData.capacity || ''}
              onChange={e => setProgramData(prev => ({
                ...prev,
                capacity: e.target.value ? parseInt(e.target.value) : null
              }))}
              placeholder={t('cbo.maximum_members_to_enroll', 'Maximum members to enroll')}
            />
          </div>

          <div>
            <Label htmlFor="funding_source">{t('cbo.funding_source', 'Funding Source')}</Label>
            <Input
              id="funding_source"
              value={programData.funding_source}
              onChange={e => setProgramData(prev => ({ ...prev, funding_source: e.target.value }))}
              placeholder="e.g., Medi-Cal, Sponsor, Grant"
            />
          </div>

          <HealthPlanPicker
            id="wizard-payer_id"
            value={programData.payer_id}
            onChange={payerId => setProgramData(prev => ({ ...prev, payer_id: payerId }))}
          />
        </div>

        <div className="space-y-4">
          <div>
            <Label htmlFor="program_manager_name">{t('cbo.program_manager_name', 'Program Manager Name')}</Label>
            <Input
              id="program_manager_name"
              value={programData.program_manager_name}
              onChange={e => setProgramData(prev => ({ ...prev, program_manager_name: e.target.value }))}
              placeholder={t('cbo.full_name', 'Full name')}
            />
          </div>

          <div>
            <Label htmlFor="program_manager_email">{t('cbo.program_manager_email', 'Program Manager Email')}</Label>
            <Input
              id="program_manager_email"
              type="email"
              value={programData.program_manager_email}
              onChange={e => setProgramData(prev => ({ ...prev, program_manager_email: e.target.value }))}
              placeholder="email@example.com"
            />
          </div>

          <div>
            <Label htmlFor="program_manager_phone">{t('cbo.program_manager_phone', 'Program Manager Phone')}</Label>
            <PhoneInput
              id="program_manager_phone"
              value={programData.program_manager_phone}
              onChange={v => setProgramData(prev => ({ ...prev, program_manager_phone: v }))}
              placeholder="(555) 555-5555"
            />
          </div>

          <div>
            <Label>{t('cbo.services_offered', 'Services Offered')}</Label>
            <div className="grid grid-cols-2 gap-2 mt-2 max-h-48 overflow-y-auto p-2 border rounded-md">
              {SERVICES.map(service => (
                <label key={service} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={programData.services_offered.includes(service)}
                    onChange={() => toggleService(service)}
                    className="rounded"
                  />
                  {service.replace(/_/g, ' ')}
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSponsorMilestones = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold mb-2">{t('cbo.sponsor_milestones_terms', 'Sponsor Milestones & Terms')}</h2>
        <p className="text-gray-600">
          {t('cbo.review_the_milestones_and_incentives', 'Review the milestones and incentives defined by the sponsor')}
        </p>
      </div>

      {/* Terms Summary */}
      {sponsorTerms && (
        <Card className="p-4 bg-blue-50 border-blue-200">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            {t('cbo.payment_terms', 'Payment Terms')}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-gray-600">{t('cbo.enrollment_incentive', 'Enrollment Incentive')}</span>
              <p className="font-semibold">${sponsorTerms.base_enrollment_incentive}</p>
            </div>
            <div>
              <span className="text-gray-600">{t('cbo.per_member_rate', 'Per Member Rate')}</span>
              <p className="font-semibold">${sponsorTerms.per_member_rate || 0}</p>
            </div>
            <div>
              <span className="text-gray-600">{t('cbo.payment_schedule', 'Payment Schedule')}</span>
              <p className="font-semibold">{sponsorTerms.payment_schedule}</p>
            </div>
            <div>
              <span className="text-gray-600">{t('cbo.min_completion_rate', 'Min Completion Rate')}</span>
              <p className="font-semibold">{sponsorTerms.minimum_completion_rate || 0}%</p>
            </div>
          </div>
        </Card>
      )}

      {/* Milestones */}
      <div>
        <h3 className="font-semibold mb-3 flex items-center gap-2">
          <Award className="h-5 w-5" />
          {t('cbo.program_milestones', 'Program Milestones')}
        </h3>

        {sponsorMilestones.length === 0 ? (
          <Card className="p-6 text-center">
            <Target className="h-8 w-8 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-600">{t('cbo.no_milestones_defined_for_this', 'No milestones defined for this program yet.')}</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {sponsorMilestones.map(milestone => (
              <Card key={milestone.milestone_id} className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium">{milestone.milestone_name}</h4>
                      <Badge variant="outline">{milestone.milestone_type}</Badge>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">
                      Target: {milestone.target_value} {milestone.target_unit} - {milestone.target_metric_name}
                    </p>
                    {milestone.target_date && (
                      <p className="text-xs text-gray-500">
                        Due: {fmt.short(milestone.target_date)}
                      </p>
                    )}
                  </div>
                  {milestone.has_incentive && (
                    <div className="text-right">
                      {milestone.cbo_payout_amount > 0 && (
                        <p className="text-sm">
                          <span className="text-gray-600">CBO: </span>
                          <span className="font-semibold text-green-600">
                            ${milestone.cbo_payout_amount}
                          </span>
                        </p>
                      )}
                      {milestone.member_payout_amount > 0 && (
                        <p className="text-sm">
                          <span className="text-gray-600">{t('cbo.member', 'Member:')} </span>
                          <span className="font-semibold text-blue-600">
                            ${milestone.member_payout_amount}
                          </span>
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderWorkflowTemplateSelector = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold mb-2">{t('cbo.select_workflow_templates', 'Select Workflow Templates')}</h2>
        <p className="text-gray-600">
          Choose existing workflow templates to associate with this program.
          Templates define the automated processes for member engagement.
        </p>
      </div>

      {templatesLoading ? (
        <div className="text-center py-8">
          <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p>{t('cbo.loading_workflow_templates', 'Loading workflow templates...')}</p>
        </div>
      ) : availableTemplates.length === 0 ? (
        <Card className="p-8 text-center">
          <Workflow className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">{t('cbo.no_workflow_templates_available', 'No Workflow Templates Available')}</h3>
          <p className="text-gray-600 mb-4">
            {t('cbo.you_can_create_workflow_templates', 'You can create workflow templates in the Workflows section and then associate them with programs.')}
          </p>
          <p className="text-sm text-gray-500">
            {t('cbo.you_can_skip_this_step', 'You can skip this step and add workflows later.')}
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            {t('cbo.select_the_workflow_templates_you', 'Select the workflow templates you want to use for this program:')}
          </p>

          {availableTemplates.map(template => (
            <Card
              key={template.id}
              className={`p-4 cursor-pointer transition-all ${
                selectedTemplateIds.includes(template.id)
                  ? 'border-2 border-blue-500 bg-blue-50'
                  : 'border hover:border-blue-300'
              }`}
              onClick={() => toggleTemplateSelection(template.id)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <Workflow className="h-5 w-5 text-gray-500" />
                    <span className="font-semibold">{template.name}</span>
                    <Badge variant="outline">
                      {template.trigger_type.replace(/_/g, ' ')}
                    </Badge>
                    {template.is_active ? (
                      <Badge className="bg-green-100 text-green-800">{t('cbo.active', 'Active')}</Badge>
                    ) : (
                      <Badge variant="outline">{t('cbo.inactive', 'Inactive')}</Badge>
                    )}
                  </div>
                  {template.description && (
                    <p className="text-gray-600 text-sm mb-2">{template.description}</p>
                  )}
                  {template.program_name && (
                    <p className="text-xs text-gray-500">
                      Currently linked to: {template.program_name}
                    </p>
                  )}
                </div>
                <div className="ml-4">
                  {selectedTemplateIds.includes(template.id) ? (
                    <CheckCircle2 className="h-6 w-6 text-blue-500" />
                  ) : (
                    <Circle className="h-6 w-6 text-gray-300" />
                  )}
                </div>
              </div>
            </Card>
          ))}

          {selectedTemplateIds.length > 0 && (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>{selectedTemplateIds.length}</strong> template{selectedTemplateIds.length !== 1 ? 's' : ''} selected
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderReview = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold mb-2">{t('cbo.review_launch', 'Review & Launch')}</h2>
        <p className="text-gray-600">
          {t('cbo.review_your_program_configuration_before', 'Review your program configuration before launching')}
        </p>
      </div>

      {/* Program Model */}
      <Card className="p-4">
        <h3 className="font-semibold mb-3 flex items-center gap-2">
          {programModel === 'SPONSOR_LINKED' ? (
            <Link className="h-5 w-5 text-blue-500" />
          ) : (
            <Unlink className="h-5 w-5 text-green-500" />
          )}
          Program Model
        </h3>
        <p className="text-gray-600">
          {programModel === 'SPONSOR_LINKED'
            ? `Sponsor-Linked to ${selectedInvitation?.sponsor_name}'s ${selectedInvitation?.program_name}`
            : 'Independent Program'}
        </p>
      </Card>

      {/* Program Info */}
      <Card className="p-4">
        <h3 className="font-semibold mb-3 flex items-center gap-2">
          <FileText className="h-5 w-5" />
          {t('cbo.program_information_1', 'Program Information')}
        </h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-600">{t('cbo.name', 'Name:')}</span>
            <p className="font-medium">{programData.name}</p>
          </div>
          <div>
            <span className="text-gray-600">{t('cbo.type', 'Type:')}</span>
            <p className="font-medium">{programTypes.find(pt => pt.value === programData.program_type)?.label || programData.program_type}</p>
          </div>
          {programData.capacity && (
            <div>
              <span className="text-gray-600">{t('cbo.capacity', 'Capacity:')}</span>
              <p className="font-medium">{programData.capacity} members</p>
            </div>
          )}
          {programData.start_date && (
            <div>
              <span className="text-gray-600">{t('cbo.start_date_1', 'Start Date:')}</span>
              <p className="font-medium">{fmt.short(programData.start_date)}</p>
            </div>
          )}
        </div>
        {programData.services_offered.length > 0 && (
          <div className="mt-3">
            <span className="text-gray-600 text-sm">{t('cbo.services', 'Services:')}</span>
            <div className="flex flex-wrap gap-1 mt-1">
              {programData.services_offered.map(service => (
                <Badge key={service} variant="outline" className="text-xs">
                  {service.replace(/_/g, ' ')}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Selected Workflow Templates */}
      <Card className="p-4">
        <h3 className="font-semibold mb-3 flex items-center gap-2">
          <Workflow className="h-5 w-5" />
          Workflow Templates ({selectedTemplateIds.length})
        </h3>
        {selectedTemplateIds.length === 0 ? (
          <p className="text-gray-600 text-sm">{t('cbo.no_workflow_templates_selected', 'No workflow templates selected')}</p>
        ) : (
          <div className="space-y-2">
            {availableTemplates
              .filter(tmpl => selectedTemplateIds.includes(tmpl.id))
              .map(template => (
                <div key={template.id} className="flex items-center gap-2 text-sm">
                  <Workflow className="h-4 w-4 text-gray-400" />
                  <span>{template.name}</span>
                  <Badge variant="outline" className="text-xs">
                    {template.trigger_type.replace(/_/g, ' ')}
                  </Badge>
                </div>
              ))}
          </div>
        )}
      </Card>

      {/* Launch Button */}
      <div className="flex justify-center pt-4">
        <Button
          onClick={handleLaunch}
          disabled={loading}
          className="px-8 py-3 text-lg"
        >
          {loading ? (
            <>
              <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full mr-2"></div>
              {t('cbo.launching', 'Launching...')}
            </>
          ) : (
            <>
              <CheckCircle2 className="h-5 w-5 mr-2" />
              {t('cbo.launch_program', 'Launch Program')}
            </>
          )}
        </Button>
      </div>
    </div>
  );

  const currentStepConfig = WIZARD_STEPS.find(s => s.id === currentStep);

  return (
    <div className="max-w-5xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">{t('cbo.cbo_program_setup', 'CBO Program Setup')}</h1>
        <p className="text-gray-600">
          {t('cbo.create_a_new_program_to', 'Create a new program to serve your members')}
        </p>
      </div>

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          {visibleSteps.map((step, index) => {
            const StepIcon = step.icon;
            const isActive = step.id === currentStep;
            const isCompleted = step.id < currentStep;

            return (
              <React.Fragment key={step.id}>
                <div className="flex flex-col items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      isCompleted
                        ? 'bg-green-500 text-white'
                        : isActive
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {isCompleted ? (
                      <CheckCircle2 className="h-5 w-5" />
                    ) : (
                      <StepIcon className="h-5 w-5" />
                    )}
                  </div>
                  <span className={`text-xs mt-1 ${isActive ? 'font-semibold' : ''}`}>
                    {step.name}
                  </span>
                </div>
                {index < visibleSteps.length - 1 && (
                  <div
                    className={`flex-1 h-1 mx-2 ${
                      step.id < currentStep ? 'bg-green-500' : 'bg-gray-200'
                    }`}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
        <Progress value={(getCurrentVisibleStep() / visibleSteps.length) * 100} className="h-2" />
      </div>

      {/* Guidance */}
      {currentStepConfig && (
        <Card className="p-4 mb-6 bg-blue-50 border-blue-200">
          <p className="text-sm text-blue-800">{currentStepConfig.guidance}</p>
        </Card>
      )}

      {/* Error */}
      {error && (
        <Card className="p-4 mb-6 bg-red-50 border-red-200">
          <div className="flex items-center gap-2 text-red-800">
            <AlertCircle className="h-5 w-5" />
            <p>{error}</p>
          </div>
        </Card>
      )}

      {/* Step Content */}
      <Card className="p-6 mb-6">
        {renderStepContent()}
      </Card>

      {/* Navigation */}
      <div className="flex justify-between">
        <div>
          {currentStep > 1 && (
            <Button variant="outline" onClick={handleBack}>
              <ChevronLeft className="h-4 w-4 mr-2" />
              {t('cbo.back', 'Back')}
            </Button>
          )}
        </div>
        <div className="flex gap-2">
          {onCancel && (
            <Button variant="outline" onClick={onCancel}>
              {t('cbo.cancel', 'Cancel')}
            </Button>
          )}
          {currentStep < 6 && (
            <Button onClick={handleNext}>
              {t('cbo.next', 'Next')}
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CBOProgramSetupWizard;
